import VerbamailIcon from "../../../assets/svg/verbamail-icon";
import VerbamessengerIcon from "../../../assets/svg/verbamessenger-icon";
import VerbacallIcon from "../../../assets/svg/verbacall-icon";
import VerbasmsIcon from "../../../assets/svg/verbasms-icon";
import AddIcon from "../../../assets/svg/add-icon";
import LaunchIcon from "../../../assets/svg/launch-icon";
import RightArrowIcon from "../../../assets/svg/right-arrow-icon";
import StatusChangedIcon from "../../../assets/svg/status-changed-icon";
import UpdatedIcon from "../../../assets/svg/updated-icon";
import UserManagementIcon from "../../../assets/svg/user-management-icon";
import RefreshIcon from "../../../assets/svg/refresh-icon";
import { getFileTypeIcon } from "../../../utils/functions/file-icon-extractor";
import { StatusPill } from "../components/CampaignTable/components/StatusPill";
import {
  CampaignStatus,
  CampaignTableData,
} from "../components/CampaignTable/utils/types";
import {
  CallTemplateAPIResponse,
  CampaignLog,
  EmailTemplateAPIResponse,
  SmsTemplateAPIResponse,
  WhatsappTemplateAPIResponse,
} from "./types";
import { Tooltip } from "../../../components/Tooltip/Tooltip";
import {
  TEMPLATE_DETAILS_DEFAULT_VALUES,
  TEMPLATE_MODALITY_DEFAULT_VALUES,
  TEMPLATE_MODALITY_ENUM,
} from "../components/ManageTemplates/utils/constants";
import type {
  CallTemplateFormStateType,
  CallTemplateType,
  EmailTemplateFormStateType,
  EmailTemplateType,
  SmsTemplateFormStateType,
  SmsTemplateType,
  TemplateDetailsOverrides,
  TemplateFormStateType,
  TemplateTableData,
  WhatsAppTemplateFormStateType,
  WhatsAppTemplateType,
} from "../components/ManageTemplates/utils/types";
import { CampaignState } from "../../../redux/slices/createCampaignSlice";
import DeleteModalityIcon from "../../../assets/svg/delete-modality-icon";
import { WORKFLO_NAME } from "../../../utils/constants/globalConstants";
import { convertUtcToLocalString } from "../../../utils/functions/convert-to-local-timezone";

export const getOperationDescription = (log: CampaignLog) => {
  const campaignName = log.name;
  switch (log.campaign_log_operation) {
    case "create":
      return (
        <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
          <AddIcon className="w-[18px] h-[18px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
          {WORKFLO_NAME.singular}{" "}
          <Tooltip content={campaignName}>
            <span className="font-medium max-w-[300px] text-heading-text dark:text-heading-text-dk text-md truncate">
              {campaignName}
            </span>
          </Tooltip>{" "}
          was created
        </div>
      );
    case "delete":
      return (
        <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
          <DeleteModalityIcon className="w-[24px] h-[24px] shrink-0 text-verba-red-alert dark:text-verba-red-alert-dk" />
          {WORKFLO_NAME.singular}{" "}
          <Tooltip content={campaignName}>
            <span className="font-medium max-w-[300px] text-heading-text dark:text-heading-text-dk  text-md truncate">
              {campaignName}
            </span>
          </Tooltip>{" "}
          was deleted
        </div>
      );
    case "name_update":
      return (
        <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
          <UpdatedIcon className="w-[18px] h-[18px] shrink-0 text-verba-indigo dark:text-verba-indigo-dk" />
          {WORKFLO_NAME.singular} name was updated from{" "}
          <Tooltip content={log.name_update?.old_name}>
            <span className="font-medium max-w-[240px] text-heading-text dark:text-heading-text-dk  text-md truncate">
              {log.name_update?.old_name}
            </span>
          </Tooltip>{" "}
          to{" "}
          <Tooltip content={log.name_update?.new_name}>
            <span className="font-medium max-w-[240px] text-heading-text dark:text-heading-text-dk  text-md truncate">
              {log.name_update?.new_name}
            </span>
          </Tooltip>
        </div>
      );
    case "use_case_update":
      return (
        <>
          Use case was changed from{" "}
          <Tooltip content={log.execution_type_update?.old_use_case}>
            <span className="font-medium">
              {log.execution_type_update?.old_use_case}
            </span>
          </Tooltip>{" "}
          to{" "}
          <Tooltip content={log.execution_type_update?.new_use_case}>
            <span className="font-medium">
              {log.execution_type_update?.new_use_case}
            </span>
          </Tooltip>{" "}
          in <span className="font-medium ">{campaignName}</span>
        </>
      );
    case "audience_data_update": {
      const oldFileName =
        log.audience_data_update?.old_audience_data?.file_name;
      const newFileName =
        log.audience_data_update?.new_audience_data?.file_name;
      if (!oldFileName) {
        return (
          <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
            <UserManagementIcon className="w-[18px] h-[18px] shrink-0 text-verba-indigo dark:text-verba-indigo-dk" />
            <span className="font-medium text-heading-text dark:text-heading-text-dk text-md">
              Contacts
            </span>{" "}
            <Tooltip content={newFileName}>
              <span className="font-medium">{newFileName}</span>
            </Tooltip>{" "}
            added in {WORKFLO_NAME.singular}{" "}
            <Tooltip content={campaignName}>
              <span className="font-medium text-heading-text dark:text-heading-text-dk text-md">
                {campaignName}
              </span>
            </Tooltip>
          </div>
        );
      }
      return (
        <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
          <UserManagementIcon className="shrink-0 w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
          <span className="font-medium text-heading-text dark:text-heading-text-dk text-md">
            Contacts
          </span>{" "}
          updated in {WORKFLO_NAME.singular}{" "}
          <Tooltip content={campaignName}>
            <span className="font-medium text-heading-text dark:text-heading-text-dk text-md truncate">
              {campaignName}
            </span>
          </Tooltip>{" "}
          from{" "}
          <Tooltip content={oldFileName}>
            <span className="font-medium text-heading-text dark:text-heading-text-dk text-md truncate">
              {oldFileName}
            </span>
          </Tooltip>{" "}
          to{" "}
          <Tooltip content={newFileName}>
            <span className="font-medium text-heading-text dark:text-heading-text-dk text-md truncate">
              {newFileName}
            </span>
          </Tooltip>
        </div>
      );
    }
    case "reach_out_add": {
      let Icon;
      let channel;
      if (log.reach_out_added?.channel === "call") {
        Icon = (
          <VerbacallIcon className="w-[18px] h-[18px] shrink-0 text-verba-lavender dark:text-verba-lavender-dk" />
        );
        channel = "VerbaCall";
      } else if (log.reach_out_added?.channel === "email") {
        Icon = (
          <VerbamailIcon className="w-[18px] h-[18px] shrink-0 text-verba-mail dark:text-verba-mail-dk" />
        );
        channel = "VerbaMail";
      } else {
        Icon = (
          <VerbamessengerIcon className="w-[18px] h-[18px] shrink-0 text-verba-messenger dark:text-verba-messenger-dk" />
        );
        channel = "VerbaMessenger";
      }
      return (
        <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
          {Icon}
          <span className="font-medium capitalize text-heading-text dark:text-heading-text-dk text-md">
            {channel}
          </span>{" "}
          reach out added in {WORKFLO_NAME.singular}{" "}
          <Tooltip content={campaignName}>
            <span className="font-medium text-heading-text dark:text-heading-text-dk text-md">
              {campaignName}
            </span>
          </Tooltip>
        </div>
      );
    }
    case "reach_out_remove": {
      let Icon;
      let channel;
      if (log.reach_out_removed?.channel === "call") {
        Icon = (
          <VerbacallIcon className="w-[18px] h-[18px] shrink-0 text-verba-lavender dark:text-verba-lavender-dk" />
        );
        channel = "VerbaCall";
      } else if (log.reach_out_removed?.channel === "email") {
        Icon = (
          <VerbamailIcon className="w-[18px] h-[18px] shrink-0 text-verba-mail dark:text-verba-mail-dk" />
        );
        channel = "VerbaMail";
      } else {
        Icon = (
          <VerbamessengerIcon className="w-[18px] h-[18px] shrink-0 text-verba-messenger dark:text-verba-messenger-dk" />
        );
        channel = "VerbaMessenger";
      }
      return (
        <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
          {Icon}
          <span className="font-medium capitalize text-heading-text dark:text-heading-text-dk text-md">
            {channel}
          </span>{" "}
          reach out removed from {WORKFLO_NAME.singular}{" "}
          <Tooltip content={campaignName}>
            <span className="font-medium text-heading-text dark:text-heading-text-dk text-md">
              {campaignName}
            </span>
          </Tooltip>
        </div>
      );
    }
    case "status_update":
      return (
        <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
          <StatusChangedIcon className="w-[18px] h-[18px] shrink-0 text-verba-indigo dark:text-verba-indigo-dk" />
          {WORKFLO_NAME.singular}{" "}
          <Tooltip content={campaignName}>
            <span className="font-medium max-w-[300px] truncate capitalize text-heading-text dark:text-heading-text-dk text-md">
              {campaignName}
            </span>
          </Tooltip>{" "}
          status changed{" "}
          <StatusPill
            status={log.status_update?.old_status as CampaignStatus}
            outerClassName=" !h-[22px]"
          />
          <RightArrowIcon className="w-[14px] h-[14px] shrink-0 text-dark-gray dark:text-dark-gray-dk" />
          <StatusPill
            status={log.status_update?.new_status as CampaignStatus}
            outerClassName="!h-[22px]"
          />
        </div>
      );
    case "launch":
      return (
        <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
          <LaunchIcon className="w-[18px] h-[18px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
          {WORKFLO_NAME.singular}{" "}
          <Tooltip content={campaignName}>
            <span className="font-medium max-w-[300px] truncate text-heading-text dark:text-heading-text-dk text-md">
              {campaignName}
            </span>
          </Tooltip>{" "}
          launched successfully
        </div>
      );
    case "create_template":
      return (
        <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
          <img
            src={getFileTypeIcon("image")}
            alt="template icon"
            className="w-[18px] h-[18px] shrink-0"
          />
          Template
          <Tooltip content={log?.template_info?.name}>
            <span className="font-medium max-w-[300px] truncate text-heading-text dark:text-heading-text-dk text-md">
              {log?.template_info?.name}
            </span>
          </Tooltip>{" "}
          created
        </div>
      );
    case "update_template": {
      const field = log?.template_update?.[0];
      if (field) {
        return (
          <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
            <img
              src={getFileTypeIcon("image")}
              alt="template icon"
              className="w-[18px] h-[18px] shrink-0"
            />
            Template
            <Tooltip content={log?.template_info?.name}>
              <span className="font-medium max-w-[300px] truncate capitalize text-heading-text dark:text-heading-text-dk text-md">
                {log?.template_info?.name}
              </span>
            </Tooltip>
            {field} was edited
          </div>
        );
      }
      return (
        <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
          <img
            src={getFileTypeIcon("image")}
            alt="template icon"
            className="w-[18px] h-[18px] shrink-0"
          />
          Template
          <Tooltip content={log?.template_info?.name}>
            <span className="font-medium max-w-[300px] truncate text-heading-text dark:text-heading-text-dk text-md">
              {log?.template_info?.name}
            </span>
          </Tooltip>{" "}
          edited
        </div>
      );
    }
    case "delete_template":
      return (
        <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
          <DeleteModalityIcon className="w-[24px] h-[24px] shrink-0 text-verba-red-alert dark:text-verba-red-alert-dk" />
          Template
          <Tooltip content={log?.template_info?.name}>
            <span className="font-medium max-w-[300px] truncate text-heading-text dark:text-heading-text-dk text-md">
              {log?.template_info?.name}
            </span>
          </Tooltip>{" "}
          deleted
        </div>
      );
    case "upload_contacts_csv":
      return (
        <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
          <img
            src={getFileTypeIcon("csv")}
            alt="csv icon"
            className="w-[18px] h-[18px] shrink-0"
          />
          <Tooltip content={log?.contact_csv_info?.name}>
            <span className="font-medium max-w-[300px] truncate text-heading-text dark:text-heading-text-dk text-md">
              {log?.contact_csv_info?.name}
            </span>
          </Tooltip>{" "}
          uploaded
        </div>
      );
    case "download_contacts_csv":
      return (
        <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
          <img
            src={getFileTypeIcon("csv")}
            alt="csv icon"
            className="w-[18px] h-[18px] shrink-0"
          />
          <Tooltip content={log?.contact_csv_info?.name}>
            <span className="font-medium max-w-[300px] truncate text-heading-text dark:text-heading-text-dk text-md">
              {log?.contact_csv_info?.name}
            </span>
          </Tooltip>{" "}
          downloaded
        </div>
      );
    case "delete_contacts_csv":
      return (
        <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
          <DeleteModalityIcon className="w-[24px] h-[24px] shrink-0 text-verba-red-alert dark:text-verba-red-alert-dk" />
          <Tooltip content={log?.contact_csv_info?.name}>
            <span className="font-medium max-w-[300px] truncate text-heading-text dark:text-heading-text-dk text-md">
              {log?.contact_csv_info?.name}
            </span>
          </Tooltip>{" "}
          deleted
        </div>
      );
    case "retry_campaign":
      return (
        <div className="flex items-center flex-wrap gap-[8px]">
          <div className="flex items-center flex-wrap gap-[4px] text-dark-gray dark:text-dark-gray-dk">
            <RefreshIcon className="w-[18px] h-[18px] shrink-0 text-retrying-status dark:text-retrying-status-dk" />
            Retrying {WORKFLO_NAME.singular}{" "}
            <Tooltip content={campaignName}>
              <span className="font-medium max-w-[300px] truncate capitalize text-heading-text dark:text-heading-text-dk text-md">
                {campaignName}
              </span>
            </Tooltip>{" "}
          </div>
          <div className="bg-border-gray dark:bg-border-gray-dk w-px h-[19px] shrink-0"></div>
          <StatusPill
            status={"retry_in_progress" as CampaignStatus}
            outerClassName=" !h-[22px]"
          />
        </div>
      );
    default:
      return (
        <>
          {log.campaign_log_operation} operation on{" "}
          <span className="font-medium ">{campaignName}</span>
        </>
      );
  }
};

export const csvToJson = (csvString: string) => {
  const lines = csvString.trim().split("\n");
  const headers = lines[0].split(",");
  const result: Record<string, string>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(",");
    const obj: Record<string, string> = {};

    for (let j = 0; j < headers.length; j++) {
      obj[headers[j].trim()] = values[j].trim();
    }
    result.push(obj);
  }
  return JSON.stringify(result, null, 2);
};

export const getChannelIcon = (channel: string, className?: string) => {
  switch (channel.toLowerCase()) {
    case "email":
      return (
        <VerbamailIcon
          className={`w-[14px] h-[14px] text-verba-mail dark:text-verba-mail-dk ${className}`}
        />
      );
    case "whatsapp":
      return (
        <VerbamessengerIcon
          className={`w-[14px] h-[14px] text-verba-messenger dark:text-verba-messenger-dk ${className}`}
        />
      );
    case "call":
      return (
        <VerbacallIcon
          className={`w-[14px] h-[14px] text-verba-lavender dark:text-verba-lavender-dk ${className}`}
        />
      );
    // case "sms":
    //   return (
    //     <VerbasmsIcon
    //       className={`w-[14px] h-[14px] text-verba-sms dark:text-verba-sms-dk ${className}`}
    //     />
    //   );
    default:
      return null;
  }
};

export const getModalityDisplayName = (modality: string) => {
  switch (modality.toLowerCase()) {
    case "call":
      return "VerbaCall";
    case "email":
      return "VerbaMail";
    case "whatsapp":
      return "VerbaMessenger";
    // case "sms":
    //   return "VerbaSMS";
    default:
      return modality;
  }
};

export const getTemplateDetailsDefaultValues = (
  modality: TEMPLATE_MODALITY_ENUM
) => {
  switch (modality) {
    case "call":
      return { ...TEMPLATE_DETAILS_DEFAULT_VALUES.call };
    case "whatsapp":
      return { ...TEMPLATE_DETAILS_DEFAULT_VALUES.whatsapp };
    case "sms":
      return { ...TEMPLATE_DETAILS_DEFAULT_VALUES.sms };
    case "email":
    default:
      return { ...TEMPLATE_DETAILS_DEFAULT_VALUES.email };
  }
};

const getTemplateModalityDefaultValues = (modality: TEMPLATE_MODALITY_ENUM) => {
  switch (modality) {
    case "call":
      return { ...TEMPLATE_MODALITY_DEFAULT_VALUES.call };
    case "whatsapp":
      return { ...TEMPLATE_MODALITY_DEFAULT_VALUES.whatsapp };
    case "sms":
      return { ...TEMPLATE_MODALITY_DEFAULT_VALUES.sms };
    case "email":
    default:
      return { ...TEMPLATE_MODALITY_DEFAULT_VALUES.email };
  }
};

export const getTemplateDisplayName = (payload: TemplateFormStateType) => {
  switch (payload.templateDetail.modality) {
    case "email":
    case "call":
    case "sms":
      return payload.templateDetail.templateName;
    case "whatsapp":
      return (payload.modalityData as WhatsAppTemplateType).templateName;
  }
};

export const formatTemplateData = (
  templateData: TemplateTableData | null,
  overrides?: TemplateDetailsOverrides
): TemplateFormStateType => {
  if (templateData) {
    const emailTemplateData = templateData as EmailTemplateAPIResponse;
    const callTemplateData = templateData as CallTemplateAPIResponse;
    const whatsappTemplateData = templateData as WhatsappTemplateAPIResponse;
    const smsTemplateData = templateData as SmsTemplateAPIResponse;

    switch (templateData.modality) {
      case "email":
        return {
          templateDetail: {
            ...TEMPLATE_DETAILS_DEFAULT_VALUES.email,
            templateName: templateData.title || "",
            category: templateData.category || "",
            useCase: templateData.use_case || "",
            branch: templateData.branch || null,
            subject: emailTemplateData.subject || "",
          },
          modalityData: {
            emailContent: emailTemplateData.content,
            emailBuilderDesign: emailTemplateData.builder_design,
          },
        } as EmailTemplateFormStateType;
      case "call":
        return {
          templateDetail: {
            ...TEMPLATE_DETAILS_DEFAULT_VALUES.call,
            templateName: templateData.title || "",
            category: templateData.category || "",
            useCase: templateData.use_case || "",
            branch: templateData.branch || null,
          },
          modalityData: {
            assistantName: callTemplateData.assistant_name,
            initialMessage: callTemplateData.initial_message,
            endCallMessage: callTemplateData.end_message,
            voiceUrl: callTemplateData.voice_url || "",
            voiceId: callTemplateData.voice_id,
            callContext: callTemplateData.call_context,
            callScript: callTemplateData.call_script,
            voice_speed: callTemplateData.voice_speed || "",
            voice_provider: callTemplateData.voice_provider || "",
          },
        } as CallTemplateFormStateType;
      case "sms":
        return {
          templateDetail: {
            ...TEMPLATE_DETAILS_DEFAULT_VALUES.sms,
            templateName: templateData.title || "",
            category: templateData.category || "",
            useCase: templateData.use_case || "",
            branch: templateData.branch || null,
          },
          modalityData: {
            smsContent: smsTemplateData.content || "",
          },
        } as SmsTemplateFormStateType;
      case "whatsapp":
      default:
        return {
          templateDetail: {
            ...TEMPLATE_DETAILS_DEFAULT_VALUES.whatsapp,
            category: templateData.category || "",
            useCase: templateData.use_case || "",
            branch: templateData.branch || null,
          },
          modalityData: {
            templateName: whatsappTemplateData.template_name || "",
            broadcastName: whatsappTemplateData.broadcast_name || "",
            body: whatsappTemplateData.body || "",
            buttons: whatsappTemplateData.buttons || [],
            footer: whatsappTemplateData.footer || null,
            header: whatsappTemplateData.header || null,
            watiCategory: whatsappTemplateData.wati_category ?? null,
            language: whatsappTemplateData.language ?? "",
          },
        } as WhatsAppTemplateFormStateType;
    }
  }

  const selectedModality = overrides?.modality || TEMPLATE_MODALITY_ENUM.EMAIL;
  const templateDetailDefaults =
    getTemplateDetailsDefaultValues(selectedModality);

  return {
    templateDetail: {
      ...templateDetailDefaults,
      ...(overrides || {}),
      modality: selectedModality,
    } as TemplateFormStateType["templateDetail"],
    modalityData: getTemplateModalityDefaultValues(selectedModality),
  } as TemplateFormStateType;
};

const _cleanObject = (obj: Record<string, unknown>) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => {
      return _ && value;
    })
  );
};

export const formatTemplateFormAPIPayload = (
  payload: TemplateFormStateType
) => {
  const { templateDetail } = payload;

  if (!templateDetail) {
    return null;
  }

  const baseDetails = {
    title: getTemplateDisplayName(payload),
    modality: templateDetail.modality,
    category: templateDetail.category,
    use_case: templateDetail.useCase,
    branch: templateDetail.branch,
  };

  let formattedPayload = {};

  switch (templateDetail.modality) {
    case "email": {
      const modalityData = payload.modalityData as EmailTemplateType;
      formattedPayload = {
        subject: templateDetail.subject,
        content: modalityData.emailContent,
        builder_design: modalityData.emailBuilderDesign,
      };
      break;
    }
    case "call": {
      const modalityData = payload.modalityData as CallTemplateType;
      formattedPayload = {
        assistant_name: modalityData.assistantName,
        initial_message: modalityData.initialMessage,
        end_message: modalityData.endCallMessage,
        voice_url: modalityData.voiceUrl || "",
        voice_id: modalityData.voiceId,
        call_context: modalityData.callContext,
        call_script: modalityData.callScript,
        voice_speed: modalityData.voice_speed || "",
        voice_provider: modalityData.voice_provider || "",
      };
      break;
    }
    case "whatsapp": {
      const modalityData = payload.modalityData as WhatsAppTemplateType;
      formattedPayload = {
        template_name: modalityData.templateName,
        broadcast_name: modalityData.broadcastName,
        language: modalityData.language,
        body: modalityData.body,
        buttons: modalityData.buttons ?? [],
        footer: modalityData.footer,
        header:
          modalityData.header && typeof modalityData.header === "object"
            ? modalityData.header
            : null,
        wati_category: modalityData.watiCategory,
      };
      break;
    }
    case "sms": {
      const modalityData = payload.modalityData as SmsTemplateType;
      formattedPayload = {
        content: modalityData.smsContent,
      };
      break;
    }
  }

  return { ..._cleanObject(baseDetails), ...formattedPayload };
};

export const checkIfCampaignSliceEmpty = (campaign: CampaignState): boolean => {
  return (
    !campaign.id &&
    !campaign.category &&
    !campaign.name &&
    !campaign.use_case &&
    !campaign.branch &&
    !campaign.timezone &&
    campaign.audience_data?.audience_count === 0 &&
    !campaign.audience_data?.audience_data_link &&
    !campaign.audience_data?.file_name &&
    !campaign.audience_data?.file_key &&
    !campaign.audience_data?.file_id &&
    campaign.modality_info.length === 0 &&
    !Object.values(campaign.completedSteps).some((step) => step)
  );
};

export const getLatestCompletedStep = (rowData: CampaignTableData): string => {
  const hasCategory = !!rowData.use_case;
  const hasDetails = !!rowData.campaign_name && !!rowData.use_case;
  const hasReachouts = !!rowData.timezone && rowData.modalities?.length > 0;
  const hasReachoutConfig = rowData.modalities && rowData.modalities.length > 0;
  const hasContacts = !!(
    rowData.audience_data?.file_key || rowData.audience_data?.audience_data_link
  );

  if (!hasCategory) return "category";
  if (!hasDetails) return "details";
  if (!hasReachouts) return "reachouts";
  if (!hasReachoutConfig) return "reachout-config";
  if (!hasContacts) return "contacts";
  return "preview";
};

export const enrichModalityInfo = (
  modalityItems: CampaignState["modality_info"],
  timezone: string
) => {
  if (modalityItems.length === 0) return [];
  return modalityItems.map((item) => {
    let startDate = item.start_date || "";
    let startTime = item.start_time || "";
    let zonedTime = "";
    if (item.run_time) {
      zonedTime =
        convertUtcToLocalString(
          item.run_time,
          "yyyy-MM-dd HH:mm:ss",
          timezone
        ) || "";
      if (zonedTime) {
        const [datePart, timePart] = zonedTime.split(" ");
        startDate = datePart;
        startTime = timePart ? timePart.slice(0, 5) : "";
      }
    }

    return {
      ...item,
      start_date: startDate,
      start_time: startTime,
      is_saved: item.is_saved !== undefined ? item.is_saved : true,
      run_time: item.run_time,
      is_edited: item.is_edited !== undefined ? item.is_edited : false,
    };
  });
};

export const scrollToTableTop = () => {
  try {
    const tableContainer = document.querySelector(
      "[data-testid='table-container']"
    );
    (tableContainer as HTMLElement)?.scrollIntoView({
      behavior: "instant",
      block: "start",
    });
  } catch (e) {
    console.warn("Error while scrolling to top:", e);
  }
};
